export interface PollUntilOptions {
	/** Delay between checks. Defaults to 5s. */
	intervalMs?: number
	/** Total time budget before giving up. Defaults to 2min. */
	timeoutMs?: number
}

/**
 * Polls `check` until it returns a value or the time budget elapses (then returns null).
 * `null`/`undefined` or a thrown error from `check` means "not ready yet — keep polling".
 * For reconciling operations that continue server-side after a client-side timeout.
 */
export async function pollUntil<T>(
	check: () => Promise<T | null | undefined>,
	options?: PollUntilOptions,
): Promise<T | null> {
	const intervalMs = options?.intervalMs && options.intervalMs > 0 ? options.intervalMs : 5000
	const timeoutMs = options?.timeoutMs && options.timeoutMs > 0 ? options.timeoutMs : 120000
	const deadline = Date.now() + timeoutMs
	let lastError: unknown = null

	while (Date.now() < deadline) {
		try {
			const result = await check()
			if (result !== null && result !== undefined) {
				return result
			}
		} catch (error) {
			// Transient failure — keep polling until the deadline.
			lastError = error
		}
		const remainingMs = deadline - Date.now()
		if (remainingMs <= 0) {
			break
		}
		await new Promise(resolve => setTimeout(resolve, Math.min(intervalMs, remainingMs)))
	}

	if (lastError !== null) {
		console.error('pollUntil gave up after its time budget; last check error:', lastError)
	}
	return null
}
